import { environment } from '../../environments/environment';
import { getAccessToken, getDeviceId } from './session-auth.store';

/**
 * Lightweight, dependency-free client diagnostics.
 *
 * Why this exists: a Play-Store / signed RELEASE Android WebView has
 * `setWebContentsDebuggingEnabled(false)`, so `chrome://inspect` shows nothing and
 * you cannot read the JS console on a deployed build. This captures console output,
 * uncaught errors, HTTP/WebSocket traffic and a state snapshot, shows them in an
 * on-screen overlay (revealed by 5 quick taps in the top-left corner, or `?debug=1` in dev/staging),
 * and best-effort ships errors to the API (`/client_log`) so they also appear in the
 * backend logs.
 *
 * Secrets (tokens, API keys, km_meta) are redacted to their length before they are
 * stored, rendered, copied or shipped.
 */
export interface DebugLogEntry {
    ts: string;
    level: 'log' | 'info' | 'warn' | 'error';
    tag: string;
    message: string;
    data?: any;
}

export interface NetworkEntry {
    id: number;
    kind: 'fetch' | 'xhr' | 'ws';
    method: string;
    url: string;
    startedAt: string;
    state: 'pending' | 'done' | 'failed';
    status?: number;
    statusText?: string;
    durationMs?: number;
    requestHeaders?: Record<string, string>;
    requestBody?: string;
    responseHeaders?: Record<string, string>;
    responseBody?: string;
    error?: string;
    ws?: {
        sent: number;
        received: number;
        messageTypes: Record<string, number>;
        closeCode?: number;
        closeReason?: string;
        wasClean?: boolean;
    };
}

type OverlayTab = 'logs' | 'network' | 'state';

const SECRET_KEY_RE = /^(token|access_token|refresh_token|id_token|km_meta|kmMeta|api[-_]?key|apikey|password|secret|client_secret|authorization|jwt|encrypted_value)$/i;
const SECRET_HEADER_RE = /^(authorization|api-key|x-api-key|cookie|set-cookie)$/i;
const MAX_BODY_CHARS = 4000;
const MAX_INLINE_STRING = 500;

function maskSecret(value: unknown): string {
    const len = typeof value === 'string' ? value.length : String(value ?? '').length;
    return `<redacted len=${len}>`;
}

function truncate(text: string, max = MAX_BODY_CHARS): string {
    return text.length > max ? `${text.slice(0, max)}… (+${text.length - max} chars)` : text;
}

function redactValue(value: any, depth = 0): any {
    if (typeof value === 'string') {
        return value.length > MAX_INLINE_STRING ? truncate(value, 200) : value;
    }
    if (depth > 8 || value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(v => redactValue(v, depth + 1));
    }
    const out: Record<string, any> = {};
    for (const [key, v] of Object.entries(value)) {
        out[key] = SECRET_KEY_RE.test(key) && v != null && v !== '' ? maskSecret(v) : redactValue(v, depth + 1);
    }
    return out;
}

function redactUrl(raw: string): string {
    try {
        const url = new URL(raw, window.location.href);
        const secretKeys: string[] = [];
        url.searchParams.forEach((_v, key) => {
            if (SECRET_KEY_RE.test(key)) {
                secretKeys.push(key);
            }
        });
        secretKeys.forEach(key => url.searchParams.set(key, `redacted-len-${(url.searchParams.get(key) || '').length}`));
        return url.toString();
    } catch {
        return String(raw);
    }
}

function redactHeaders(headers: Record<string, string> | undefined): Record<string, string> | undefined {
    if (!headers) {
        return undefined;
    }
    const out: Record<string, string> = {};
    for (const [name, value] of Object.entries(headers)) {
        if (SECRET_HEADER_RE.test(name)) {
            out[name] = /^Bearer /i.test(value) ? `Bearer ${maskSecret(value.slice(7))}` : maskSecret(value);
        } else {
            out[name] = value;
        }
    }
    return out;
}

function redactBody(body: unknown): string | undefined {
    if (body == null || body === '') {
        return undefined;
    }
    if (typeof body !== 'string') {
        if (typeof FormData !== 'undefined' && body instanceof FormData) return '[FormData]';
        if (typeof Blob !== 'undefined' && body instanceof Blob) return `[Blob ${body.type || 'binary'} ${body.size}B]`;
        if (body instanceof ArrayBuffer) return `[ArrayBuffer ${body.byteLength}B]`;
        if (ArrayBuffer.isView(body)) return `[${body.constructor.name} ${body.byteLength}B]`;
        if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return '[URLSearchParams]';
        try {
            return truncate(JSON.stringify(redactValue(body)));
        } catch {
            return '[unserializable body]';
        }
    }
    try {
        return truncate(JSON.stringify(redactValue(JSON.parse(body))));
    } catch {
        return truncate(body);
    }
}

function redactWsProtocols(protocols?: string | string[]): string[] {
    const list = Array.isArray(protocols) ? protocols : protocols ? [protocols] : [];
    return list.map((p, i) => {
        if (p.startsWith('openai-insecure-api-key.')) return 'openai-insecure-api-key.<redacted>';
        if (list[i - 1] === 'token' || p.length > 32) return maskSecret(p);
        return p;
    });
}

function messageType(data: string): string | undefined {
    const match = /"type"\s*:\s*"([^"]+)"/.exec(data.slice(0, 300));
    return match?.[1];
}

function shortUrl(raw: string): string {
    try {
        const url = new URL(raw, window.location.href);
        return url.origin === window.location.origin ? url.pathname : `${url.host}${url.pathname}`;
    } catch {
        return raw;
    }
}

function jsonReplacer(_key: string, value: any): any {
    if (value instanceof Error) {
        return { name: value.name, message: value.message, stack: value.stack };
    }
    if (typeof CloseEvent !== 'undefined' && value instanceof CloseEvent) {
        return { type: value.type, code: value.code, reason: value.reason, wasClean: value.wasClean };
    }
    if (typeof Event !== 'undefined' && value instanceof Event) {
        return { type: value.type, isTrusted: value.isTrusted };
    }
    return value;
}

class DebugLog {
    private readonly maxEntries = 500;
    private readonly maxNetworkEntries = 200;
    private entries: DebugLogEntry[] = [];
    private network: NetworkEntry[] = [];
    private networkSeq = 0;
    private expandedNetworkIds = new Set<number>();
    private stateValues: Record<string, unknown> = {};
    private installed = false;
    private overlayVisible = false;
    private activeTab: OverlayTab = 'logs';
    private tapTimes: number[] = [];
    private networkRenderTimer: ReturnType<typeof setTimeout> | null = null;

    private rootEl: HTMLDivElement | null = null;
    private listEl: HTMLDivElement | null = null;
    private networkEl: HTMLDivElement | null = null;
    private stateEl: HTMLDivElement | null = null;
    private countEl: HTMLSpanElement | null = null;
    private tabButtons: Partial<Record<OverlayTab, HTMLButtonElement>> = {};

    private readonly orig = {
        log: console.log.bind(console),
        info: (console.info || console.log).bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console)
    };

    /** Install global hooks. Safe to call multiple times. */
    install(): void {
        if (this.installed || typeof window === 'undefined') {
            return;
        }
        this.installed = true;

        this.patchConsole();
        this.patchFetch();
        this.patchXhr();
        this.patchWebSocket();

        window.addEventListener('error', event => {
            const err = (event as ErrorEvent).error;
            this.error('window.onerror', (event as ErrorEvent).message || 'Uncaught error', this.serializeError(err) ?? {
                filename: (event as ErrorEvent).filename,
                lineno: (event as ErrorEvent).lineno,
                colno: (event as ErrorEvent).colno
            });
        });

        window.addEventListener('unhandledrejection', event => {
            this.error('unhandledrejection', 'Unhandled promise rejection', this.serializeError((event as PromiseRejectionEvent).reason));
        });

        window.addEventListener('online', () => this.info('network', 'Device went online'));
        window.addEventListener('offline', () => this.warn('network', 'Device went offline'));
        document.addEventListener('visibilitychange', () => this.info('app', `Visibility changed: ${document.visibilityState}`));

        if (environment.allowAdminUrlFlags) {
            try {
                const params = new URLSearchParams(window.location.search);
                if (params.get('debug') === '1' || localStorage.getItem('gfh_debug') === '1') {
                    this.show();
                }
            } catch {
                /* ignore */
            }
        }

        this.installTapGesture();
        this.info('debug', 'Diagnostics ready', {
            apiUrl: environment.apiUrl,
            environment: environment.environmentName,
            userAgent: navigator.userAgent,
            online: navigator.onLine
        });
    }

    log(tag: string, message: any, data?: any): void {
        this.record('log', tag, message, data);
    }

    info(tag: string, message: any, data?: any): void {
        this.record('info', tag, message, data);
    }

    warn(tag: string, message: any, data?: any): void {
        this.record('warn', tag, message, data);
    }

    error(tag: string, message: any, data?: any): void {
        this.record('error', tag, message, data);
        this.ship(this.entries[this.entries.length - 1]);
    }

    /** Publish a named section to the State tab (values are redacted like request bodies). */
    setState(section: string, value: unknown): void {
        this.stateValues[section] = this.safeData(redactValue(value));
        if (this.overlayVisible && this.activeTab === 'state') {
            void this.renderState();
        }
    }

    /** Normalize an HttpErrorResponse / Error / unknown into something printable. */
    describeHttpError(error: any): Record<string, any> {
        if (!error) {
            return { note: 'no error object' };
        }
        return {
            name: error.name,
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            message: error.message,
            // status 0 almost always means network/TLS/CORS failure (request never completed)
            likelyCause: error.status === 0 || error.status == null ? 'network/TLS/CORS (request did not reach server)' : 'server returned an error status',
            serverDetail: error.error?.detail ?? error.error
        };
    }

    /** Point-in-time view of auth, voice, storage and device capabilities. Never includes secret values. */
    async snapshot(): Promise<Record<string, unknown>> {
        const kmMeta = this.readStorage(() => localStorage.getItem('km_meta'));
        const accessToken = getAccessToken();
        const tokenExp = this.readJwtExp(accessToken);

        let micPermission = 'unsupported';
        try {
            const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
            micPermission = status?.state ?? 'unsupported';
        } catch {
            /* not supported in this WebView */
        }

        let storagePersisted: boolean | string = 'unsupported';
        try {
            if (navigator.storage?.persisted) {
                storagePersisted = await navigator.storage.persisted();
            }
        } catch {
            /* ignore */
        }

        return {
            app: {
                environment: environment.environmentName,
                apiUrl: environment.apiUrl,
                conversationMode: (environment as any).conversationMode,
                page: window.location.origin + window.location.pathname,
                online: navigator.onLine,
                visibility: document.visibilityState,
                userAgent: navigator.userAgent
            },
            auth: {
                hasAccessToken: !!accessToken,
                accessTokenExpiresAt: tokenExp ? new Date(tokenExp * 1000).toISOString() : undefined,
                accessTokenSecondsLeft: tokenExp ? Math.round(tokenExp - Date.now() / 1000) : undefined,
                deviceId: getDeviceId()
            },
            voice: {
                hasKmMeta: !!kmMeta,
                kmMetaLength: kmMeta?.length ?? 0,
                micPermission,
                hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
                hasAudioContext: !!(window.AudioContext || (window as any).webkitAudioContext),
                hasWebSocket: typeof WebSocket !== 'undefined',
                hasWakeLock: 'wakeLock' in navigator
            },
            storage: {
                persisted: storagePersisted,
                localStorage: this.storageSummary(() => localStorage),
                sessionStorage: this.storageSummary(() => sessionStorage)
            },
            ...this.stateValues
        };
    }

    private record(level: DebugLogEntry['level'], tag: string, message: any, data?: any): void {
        const entry: DebugLogEntry = {
            ts: new Date().toISOString(),
            level,
            tag,
            message: this.stringify(message),
            data: data !== undefined ? this.safeData(data) : undefined
        };
        this.entries.push(entry);
        if (this.entries.length > this.maxEntries) {
            this.entries.shift();
        }
        this.renderEntry(entry);
    }

    private patchConsole(): void {
        const wrap = (level: DebugLogEntry['level'], original: (...a: any[]) => void) => {
            return (...args: any[]) => {
                original(...args);
                try {
                    const [first, ...rest] = args;
                    this.record(level, 'console', first, rest.length ? rest : undefined);
                } catch {
                    /* never let logging break the app */
                }
            };
        };
        console.log = wrap('log', this.orig.log);
        console.info = wrap('info', this.orig.info);
        console.warn = wrap('warn', this.orig.warn);
        console.error = wrap('error', this.orig.error);
    }

    private ship(entry: DebugLogEntry | undefined): void {
        if (!entry || !environment.apiUrl) {
            return;
        }
        try {
            fetch(environment.apiUrl + '/client_log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    level: entry.level,
                    tag: entry.tag,
                    message: entry.message,
                    data: entry.data,
                    ts: entry.ts,
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    environment: environment.environmentName
                }),
                keepalive: true
            }).catch(() => undefined);
        } catch {
            /* best effort only */
        }
    }

    // ---------- network capture ----------

    private isIgnoredUrl(url: string): boolean {
        return !url || url.includes('/client_log') || url.startsWith('data:') || url.startsWith('blob:');
    }

    private startNetwork(kind: NetworkEntry['kind'], method: string, url: string, headers?: Record<string, string>, body?: unknown): NetworkEntry {
        const entry: NetworkEntry = {
            id: ++this.networkSeq,
            kind,
            method,
            url: redactUrl(url),
            startedAt: new Date().toISOString(),
            state: 'pending',
            requestHeaders: redactHeaders(headers),
            requestBody: redactBody(body)
        };
        this.network.push(entry);
        if (this.network.length > this.maxNetworkEntries) {
            const removed = this.network.shift();
            if (removed) {
                this.expandedNetworkIds.delete(removed.id);
            }
        }
        this.networkChanged();
        return entry;
    }

    private finishNetwork(entry: NetworkEntry, startedMs: number, status: number, statusText: string, headers?: Record<string, string>): void {
        entry.status = status;
        entry.statusText = statusText;
        entry.durationMs = Math.round(performance.now() - startedMs);
        entry.responseHeaders = redactHeaders(headers);
        entry.state = status >= 400 ? 'failed' : 'done';
        this.networkChanged();
    }

    private failNetwork(entry: NetworkEntry, startedMs: number, error: unknown): void {
        entry.state = 'failed';
        entry.status = 0;
        entry.durationMs = Math.round(performance.now() - startedMs);
        entry.error = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        this.networkChanged();
        this.reportNetworkOutcome(entry);
    }

    /** Mirror failed requests into the Logs tab (and ship errors) so they show up without opening Network. */
    private reportNetworkOutcome(entry: NetworkEntry): void {
        const label = `${entry.method} ${shortUrl(entry.url)}`;
        const summary = {
            status: entry.status,
            durationMs: entry.durationMs,
            error: entry.error,
            response: entry.responseBody ? truncate(entry.responseBody, 300) : undefined
        };
        if (entry.status === 0) {
            this.error('net', `${label} failed (status 0: network/TLS/CORS or aborted)`, summary);
        } else if ((entry.status ?? 0) >= 500) {
            this.error('net', `${label} -> ${entry.status}`, summary);
        } else if ((entry.status ?? 0) >= 400) {
            this.warn('net', `${label} -> ${entry.status}`, summary);
        }
    }

    private headersToObject(headers: HeadersInit | Headers | undefined | null): Record<string, string> | undefined {
        if (!headers) {
            return undefined;
        }
        const out: Record<string, string> = {};
        try {
            if (typeof Headers !== 'undefined' && headers instanceof Headers) {
                headers.forEach((value, key) => (out[key] = value));
            } else if (Array.isArray(headers)) {
                headers.forEach(([key, value]) => (out[key] = value));
            } else {
                Object.assign(out, headers);
            }
        } catch {
            /* ignore */
        }
        return out;
    }

    private patchFetch(): void {
        if (typeof window.fetch !== 'function') {
            return;
        }
        const originalFetch = window.fetch.bind(window);
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const isRequest = typeof Request !== 'undefined' && input instanceof Request;
            const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
            if (this.isIgnoredUrl(url)) {
                return originalFetch(input, init);
            }
            const method = (init?.method || (isRequest ? (input as Request).method : 'GET')).toUpperCase();
            const headers = this.headersToObject(init?.headers ?? (isRequest ? (input as Request).headers : undefined));
            const entry = this.startNetwork('fetch', method, url, headers, init?.body);
            const started = performance.now();
            let response: Response;
            try {
                response = await originalFetch(input, init);
            } catch (err) {
                this.failNetwork(entry, started, err);
                throw err;
            }
            try {
                this.finishNetwork(entry, started, response.status, response.statusText, this.headersToObject(response.headers));
                void this.captureFetchBody(entry, response);
            } catch {
                /* never let logging break the app */
            }
            return response;
        };
    }

    private async captureFetchBody(entry: NetworkEntry, response: Response): Promise<void> {
        const type = response.headers.get('content-type') || '';
        if (!/json|text\/plain|text\/html|problem/i.test(type) || /event-stream/i.test(type)) {
            entry.responseBody = type ? `[${type} body not captured]` : undefined;
        } else {
            try {
                const clone = response.clone();
                entry.responseBody = redactBody(await clone.text());
            } catch {
                entry.responseBody = '[body unavailable]';
            }
        }
        this.networkChanged();
        this.reportNetworkOutcome(entry);
    }

    private patchXhr(): void {
        if (typeof XMLHttpRequest === 'undefined') {
            return;
        }
        const self = this;
        const proto = XMLHttpRequest.prototype;
        const originalOpen = proto.open;
        const originalSend = proto.send;
        const originalSetHeader = proto.setRequestHeader;

        proto.open = function (this: XMLHttpRequest & { __gfhNet?: any }, method: string, url: string | URL, ...rest: any[]) {
            this.__gfhNet = { method: String(method).toUpperCase(), url: String(url), headers: {} as Record<string, string> };
            return (originalOpen as any).call(this, method, url, ...rest);
        } as typeof proto.open;

        proto.setRequestHeader = function (this: XMLHttpRequest & { __gfhNet?: any }, name: string, value: string) {
            if (this.__gfhNet) {
                this.__gfhNet.headers[name] = value;
            }
            return originalSetHeader.call(this, name, value);
        };

        proto.send = function (this: XMLHttpRequest & { __gfhNet?: any }, body?: Document | XMLHttpRequestBodyInit | null) {
            const meta = this.__gfhNet;
            if (meta && !self.isIgnoredUrl(meta.url)) {
                try {
                    const entry = self.startNetwork('xhr', meta.method, meta.url, meta.headers, body);
                    const started = performance.now();
                    let outcome = '';
                    this.addEventListener('abort', () => (outcome = 'aborted'));
                    this.addEventListener('timeout', () => (outcome = 'timed out'));
                    this.addEventListener('loadend', () => {
                        if (this.status === 0) {
                            self.failNetwork(entry, started, outcome || 'status 0 (network/TLS/CORS)');
                            return;
                        }
                        self.finishNetwork(entry, started, this.status, this.statusText, self.parseXhrHeaders(this.getAllResponseHeaders()));
                        entry.responseBody = self.readXhrBody(this);
                        self.reportNetworkOutcome(entry);
                    });
                } catch {
                    /* never let logging break the app */
                }
            }
            return originalSend.call(this, body);
        };
    }

    private parseXhrHeaders(raw: string): Record<string, string> {
        const out: Record<string, string> = {};
        raw.trim().split(/[\r\n]+/).forEach(line => {
            const index = line.indexOf(':');
            if (index > 0) {
                out[line.slice(0, index).trim().toLowerCase()] = line.slice(index + 1).trim();
            }
        });
        return out;
    }

    private readXhrBody(xhr: XMLHttpRequest): string | undefined {
        try {
            if (xhr.responseType === '' || xhr.responseType === 'text') {
                return redactBody(xhr.responseText);
            }
            if (xhr.responseType === 'json') {
                return redactBody(xhr.response);
            }
            return `[${xhr.responseType} body not captured]`;
        } catch {
            return '[body unavailable]';
        }
    }

    private patchWebSocket(): void {
        const OriginalWebSocket = window.WebSocket;
        if (!OriginalWebSocket) {
            return;
        }
        const self = this;
        const PatchedWebSocket = function (url: string | URL, protocols?: string | string[]) {
            const socket = protocols === undefined ? new OriginalWebSocket(url) : new OriginalWebSocket(url, protocols);
            try {
                self.trackWebSocket(socket, String(url), protocols);
            } catch {
                /* never let logging break the app */
            }
            return socket;
        };
        (PatchedWebSocket as any).prototype = OriginalWebSocket.prototype;
        Object.setPrototypeOf(PatchedWebSocket, OriginalWebSocket);
        window.WebSocket = PatchedWebSocket as unknown as typeof WebSocket;
    }

    private trackWebSocket(socket: WebSocket, url: string, protocols?: string | string[]): void {
        const safeProtocols = redactWsProtocols(protocols);
        const entry = this.startNetwork('ws', 'WS', url, safeProtocols.length ? { protocols: safeProtocols.join(', ') } : undefined);
        entry.ws = { sent: 0, received: 0, messageTypes: {} };
        const stats = entry.ws;
        const started = performance.now();
        const label = shortUrl(entry.url);
        let opened = false;

        const countType = (direction: string, data: unknown) => {
            const type = typeof data === 'string' ? messageType(data) ?? '[text]' : '[binary]';
            const key = `${direction} ${type}`;
            stats.messageTypes[key] = (stats.messageTypes[key] ?? 0) + 1;
            return type;
        };

        const originalSend = socket.send;
        socket.send = function (data: string | ArrayBufferLike | Blob | ArrayBufferView) {
            stats.sent++;
            countType('sent', data);
            return originalSend.call(socket, data);
        };

        socket.addEventListener('open', () => {
            opened = true;
            entry.status = 101;
            entry.statusText = 'open';
            entry.durationMs = Math.round(performance.now() - started);
            this.info('ws', `Socket open ${label}`, { connectMs: entry.durationMs });
            this.networkChanged();
        });

        socket.addEventListener('message', (event: MessageEvent) => {
            stats.received++;
            const type = countType('recv', event.data);
            if (typeof event.data === 'string' && /error/i.test(type)) {
                this.error('ws', `Server sent "${type}" on ${label}`, redactBody(event.data));
            }
            this.networkChanged();
        });

        socket.addEventListener('error', () => {
            entry.error = 'WebSocket error event';
            this.warn('ws', `Socket error on ${label}`, { opened, afterMs: Math.round(performance.now() - started) });
            this.networkChanged();
        });

        socket.addEventListener('close', (event: CloseEvent) => {
            stats.closeCode = event.code;
            stats.closeReason = event.reason;
            stats.wasClean = event.wasClean;
            const normal = opened && (event.code === 1000 || event.code === 1005);
            entry.state = normal ? 'done' : 'failed';
            if (!opened) {
                entry.status = 0;
                entry.durationMs = Math.round(performance.now() - started);
            }
            const details = {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                opened,
                lifetimeMs: Math.round(performance.now() - started),
                sent: stats.sent,
                received: stats.received
            };
            if (normal) {
                this.info('ws', `Socket closed ${label}`, details);
            } else {
                this.error('ws', opened ? `Socket closed abnormally ${label}` : `Socket failed to open ${label}`, details);
            }
            this.networkChanged();
        });
    }

    private networkChanged(): void {
        if (!this.overlayVisible || this.activeTab !== 'network' || this.networkRenderTimer) {
            this.updateCount();
            return;
        }
        this.networkRenderTimer = setTimeout(() => {
            this.networkRenderTimer = null;
            this.renderNetwork();
        }, 400);
    }

    // ---------- on-screen overlay ----------

    private installTapGesture(): void {
        window.addEventListener(
            'touchstart',
            e => {
                const t = e.touches?.[0];
                if (!t || t.clientX > 70 || t.clientY > 70) {
                    return;
                }
                const now = Date.now();
                this.tapTimes = this.tapTimes.filter(x => now - x < 1500);
                this.tapTimes.push(now);
                if (this.tapTimes.length >= 5) {
                    this.tapTimes = [];
                    this.toggle();
                }
            },
            { passive: true }
        );
    }

    toggle(): void {
        this.overlayVisible ? this.hide() : this.show();
    }

    show(): void {
        if (typeof document === 'undefined') {
            return;
        }
        this.ensureOverlay();
        if (this.rootEl) {
            this.rootEl.style.display = 'flex';
            this.overlayVisible = true;
            this.selectTab(this.activeTab);
        }
    }

    hide(): void {
        if (this.rootEl) {
            this.rootEl.style.display = 'none';
            this.overlayVisible = false;
        }
    }

    private ensureOverlay(): void {
        if (this.rootEl || typeof document === 'undefined') {
            return;
        }
        const root = document.createElement('div');
        root.style.cssText =
            'position:fixed;inset:0;z-index:2147483647;display:none;flex-direction:column;' +
            'background:rgba(10,12,18,.96);color:#e6e6e6;font:12px/1.45 monospace;';

        const bar = document.createElement('div');
        bar.style.cssText = 'display:flex;gap:6px;align-items:center;padding:8px;background:#11151f;flex:0 0 auto;flex-wrap:wrap;';
        const title = document.createElement('span');
        title.textContent = 'GFH Diagnostics';
        title.style.cssText = 'font-weight:bold;margin-right:auto;';
        this.countEl = document.createElement('span');
        this.countEl.style.cssText = 'opacity:.7;margin-right:6px;';

        bar.appendChild(title);
        bar.appendChild(this.countEl);
        bar.appendChild(this.makeBtn('Copy', () => void this.copy()));
        bar.appendChild(this.makeBtn('Send', () => void this.sendAll()));
        bar.appendChild(this.makeBtn('Clear', () => this.clear()));
        bar.appendChild(this.makeBtn('Close', () => this.hide()));

        const tabs = document.createElement('div');
        tabs.style.cssText = 'display:flex;gap:6px;padding:0 8px 8px;background:#11151f;flex:0 0 auto;';
        (['logs', 'network', 'state'] as OverlayTab[]).forEach(tab => {
            const btn = this.makeBtn(tab === 'logs' ? 'Logs' : tab === 'network' ? 'Network' : 'State', () => this.selectTab(tab));
            btn.style.flex = '1 1 0';
            this.tabButtons[tab] = btn;
            tabs.appendChild(btn);
        });

        const paneCss = 'flex:1 1 auto;overflow:auto;padding:8px;-webkit-overflow-scrolling:touch;';
        const list = document.createElement('div');
        list.style.cssText = paneCss;
        const networkPane = document.createElement('div');
        networkPane.style.cssText = paneCss + 'display:none;';
        const statePane = document.createElement('div');
        statePane.style.cssText = paneCss + 'display:none;white-space:pre-wrap;word-break:break-word;';

        root.appendChild(bar);
        root.appendChild(tabs);
        root.appendChild(list);
        root.appendChild(networkPane);
        root.appendChild(statePane);
        document.body.appendChild(root);

        this.rootEl = root;
        this.listEl = list;
        this.networkEl = networkPane;
        this.stateEl = statePane;

        // backfill anything captured before the overlay existed
        this.entries.forEach(e => this.renderEntry(e, true));
        this.updateCount();
    }

    private selectTab(tab: OverlayTab): void {
        this.activeTab = tab;
        if (this.listEl) this.listEl.style.display = tab === 'logs' ? 'block' : 'none';
        if (this.networkEl) this.networkEl.style.display = tab === 'network' ? 'block' : 'none';
        if (this.stateEl) this.stateEl.style.display = tab === 'state' ? 'block' : 'none';
        (Object.keys(this.tabButtons) as OverlayTab[]).forEach(key => {
            const btn = this.tabButtons[key];
            if (btn) {
                btn.style.background = key === tab ? '#4a6cd4' : '#2a3450';
            }
        });
        if (tab === 'network') {
            this.renderNetwork();
        } else if (tab === 'state') {
            void this.renderState();
        }
    }

    private makeBtn(label: string, onClick: () => void): HTMLButtonElement {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = 'background:#2a3450;color:#fff;border:0;border-radius:4px;padding:6px 10px;font:11px monospace;';
        b.addEventListener('click', onClick);
        return b;
    }

    private renderEntry(entry: DebugLogEntry, skipCount = false): void {
        if (!this.listEl) {
            return;
        }
        const colors: Record<string, string> = { log: '#cfd3dc', info: '#7fd1ff', warn: '#ffd166', error: '#ff6b6b' };
        const row = document.createElement('div');
        row.style.cssText = 'padding:3px 0;border-bottom:1px solid rgba(255,255,255,.06);white-space:pre-wrap;word-break:break-word;color:' + (colors[entry.level] || '#fff');
        const time = entry.ts.substring(11, 23);
        let text = `${time} [${entry.level.toUpperCase()}] ${entry.tag}: ${entry.message}`;
        if (entry.data !== undefined) {
            text += '\n    ' + this.stringify(entry.data);
        }
        row.textContent = text;
        const atBottom = this.listEl.scrollTop + this.listEl.clientHeight >= this.listEl.scrollHeight - 30;
        this.listEl.appendChild(row);
        if (atBottom) {
            this.listEl.scrollTop = this.listEl.scrollHeight;
        }
        if (!skipCount) {
            this.updateCount();
        }
    }

    private networkSummary(entry: NetworkEntry): string {
        const time = entry.startedAt.substring(11, 23);
        const status = entry.state === 'pending' && entry.status === undefined ? '…' : String(entry.status ?? '');
        const duration = entry.durationMs !== undefined ? ` ${entry.durationMs}ms` : '';
        const ws = entry.ws ? ` ↑${entry.ws.sent} ↓${entry.ws.received}${entry.ws.closeCode !== undefined ? ` closed ${entry.ws.closeCode}` : ''}` : '';
        return `${time} ${entry.method} ${status}${duration}${ws} ${shortUrl(entry.url)}`;
    }

    private networkDetails(entry: NetworkEntry): string {
        const lines = [`URL: ${entry.url}`, `Type: ${entry.kind}   State: ${entry.state}`];
        if (entry.status !== undefined) lines.push(`Status: ${entry.status} ${entry.statusText ?? ''}`.trim());
        if (entry.durationMs !== undefined) lines.push(`Duration: ${entry.durationMs} ms`);
        if (entry.error) lines.push(`Error: ${entry.error}`);
        if (entry.requestHeaders) lines.push(`Request headers: ${this.stringify(entry.requestHeaders)}`);
        if (entry.requestBody) lines.push(`Request body: ${entry.requestBody}`);
        if (entry.responseHeaders) lines.push(`Response headers: ${this.stringify(entry.responseHeaders)}`);
        if (entry.responseBody) lines.push(`Response body: ${entry.responseBody}`);
        if (entry.ws) lines.push(`WebSocket: ${this.stringify(entry.ws)}`);
        return lines.join('\n');
    }

    private renderNetwork(): void {
        if (!this.networkEl) {
            return;
        }
        this.updateCount();
        this.networkEl.innerHTML = '';
        if (!this.network.length) {
            this.networkEl.textContent = 'No requests captured yet.';
            return;
        }
        const hint = document.createElement('div');
        hint.textContent = 'Newest first. Tap a request for headers and bodies.';
        hint.style.cssText = 'opacity:.6;padding-bottom:6px;';
        this.networkEl.appendChild(hint);

        for (let i = this.network.length - 1; i >= 0; i--) {
            const entry = this.network[i];
            const color = entry.state === 'failed' ? '#ff6b6b' : entry.state === 'pending' ? '#ffd166' : '#7ee787';
            const row = document.createElement('div');
            row.style.cssText = 'padding:4px 0;border-bottom:1px solid rgba(255,255,255,.06);white-space:pre-wrap;word-break:break-word;color:' + color;
            row.textContent = this.networkSummary(entry);
            if (this.expandedNetworkIds.has(entry.id)) {
                const details = document.createElement('div');
                details.style.cssText = 'color:#cfd3dc;padding:4px 0 2px 10px;';
                details.textContent = this.networkDetails(entry);
                row.appendChild(details);
            }
            row.addEventListener('click', () => {
                if (this.expandedNetworkIds.has(entry.id)) {
                    this.expandedNetworkIds.delete(entry.id);
                } else {
                    this.expandedNetworkIds.add(entry.id);
                }
                this.renderNetwork();
            });
            this.networkEl.appendChild(row);
        }
    }

    private async renderState(): Promise<void> {
        if (!this.stateEl) {
            return;
        }
        const snapshot = await this.snapshot();
        this.stateEl.innerHTML = '';
        const refresh = this.makeBtn('Refresh', () => void this.renderState());
        refresh.style.marginBottom = '8px';
        const body = document.createElement('div');
        body.textContent = JSON.stringify(snapshot, null, 2);
        this.stateEl.appendChild(refresh);
        this.stateEl.appendChild(body);
    }

    private updateCount(): void {
        if (this.countEl) {
            this.countEl.textContent = `${this.entries.length} logs · ${this.network.length} requests`;
        }
    }

    private async copy(): Promise<void> {
        let text: string;
        if (this.activeTab === 'network') {
            text = this.network.map(e => `${this.networkSummary(e)}\n${this.networkDetails(e)}`).join('\n\n');
        } else if (this.activeTab === 'state') {
            text = JSON.stringify(await this.snapshot(), null, 2);
        } else {
            text = this.entries.map(e => `${e.ts} [${e.level}] ${e.tag}: ${e.message}${e.data !== undefined ? ' | ' + this.stringify(e.data) : ''}`).join('\n');
        }
        try {
            await navigator.clipboard?.writeText(text);
        } catch {
            /* ignore */
        }
    }

    private async sendAll(): Promise<void> {
        this.entries.forEach(e => this.ship(e));
        this.ship({
            ts: new Date().toISOString(),
            level: 'info',
            tag: 'debug/state',
            message: 'State snapshot',
            data: await this.snapshot()
        });
        this.ship({
            ts: new Date().toISOString(),
            level: 'info',
            tag: 'debug/network',
            message: 'Network summary',
            data: this.network.map(e => (e.state === 'failed' ? { summary: this.networkSummary(e), details: this.networkDetails(e) } : this.networkSummary(e)))
        });
    }

    private clear(): void {
        if (this.activeTab === 'network') {
            this.network = [];
            this.expandedNetworkIds.clear();
            this.renderNetwork();
            return;
        }
        this.entries = [];
        if (this.listEl) {
            this.listEl.innerHTML = '';
        }
        this.updateCount();
    }

    // ---------- helpers ----------

    private readStorage(read: () => string | null): string | null {
        try {
            return read();
        } catch {
            return null;
        }
    }

    private storageSummary(getStorage: () => Storage): Record<string, string> | string {
        try {
            const storage = getStorage();
            const out: Record<string, string> = {};
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key) {
                    out[key] = `${(storage.getItem(key) || '').length} chars`;
                }
            }
            return out;
        } catch {
            return 'unavailable';
        }
    }

    private readJwtExp(token: string): number | undefined {
        try {
            const payload = token.split('.')[1];
            if (!payload) {
                return undefined;
            }
            const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            return typeof json.exp === 'number' ? json.exp : undefined;
        } catch {
            return undefined;
        }
    }

    private serializeError(err: any): Record<string, any> | undefined {
        if (!err) {
            return undefined;
        }
        if (err instanceof Error) {
            return { name: err.name, message: err.message, stack: err.stack };
        }
        return this.safeData(err);
    }

    private stringify(value: any): string {
        if (typeof value === 'string') {
            return value;
        }
        try {
            return JSON.stringify(value, jsonReplacer);
        } catch {
            return String(value);
        }
    }

    private safeData(data: any): any {
        try {
            return JSON.parse(JSON.stringify(data, jsonReplacer));
        } catch {
            return String(data);
        }
    }
}

export const debugLog = new DebugLog();
